import React from 'react';
import { User, Sparkles, Target, BookOpen, Frown, Route, MapPin, AlertTriangle, Briefcase, Smartphone, Calendar, Users } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * PersonaPreviewCard - New two-column layout persona card
 * Layout: Large photo on left with name/quote overlay, structured data on right
 * Used both in the persona edit page (as preview) and in the personas listing page
 * 
 * @param {Object} persona - The persona data object
 * @param {Function} onClick - Optional click handler (for listing page navigation)
 * @param {string} className - Additional CSS classes for the container
 * @param {string} exportId - Optional ID for PDF export capture
 */
export const PersonaPreviewCard = ({ persona, onClick, className = '', exportId, expanded = false }) => {
  const isClickable = !!onClick;
  const isGa4Based = !!persona?.ga4_property_id;

  // Check if we have any data to show in each section
  const hasDemographics = persona?.age_range || persona?.gender || persona?.location || persona?.job_role || persona?.device_preference;
  const hasGoals = persona?.goals?.length > 0;
  const hasHabits = persona?.learning_habits?.length > 0;
  const hasFrustrations = persona?.frustrations?.length > 0;
  const hasJourneyPages = persona?.journey_key_pages?.length > 0;
  const hasJourneyEntry = persona?.journey_entry_context;
  const hasJourneyCritical = persona?.journey_critical_point;

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'P';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Card content - two-column layout
  const cardContent = (
    <div className={`flex flex-col md:flex-row min-h-[320px] bg-white ${expanded ? 'persona-expanded' : ''}`}>
      {/* Left column - Photo with overlay */}
      <div className="relative flex-shrink-0 w-full md:w-48 lg:w-56 h-64 md:h-auto md:min-h-[320px] bg-gradient-to-br from-primary/20 to-primary/5 overflow-hidden">
        {/* Background image or gradient */}
        {persona?.avatar_url ? (
          <img 
            src={persona.avatar_url} 
            alt={persona?.name || 'Persona'} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center border-4 border-white/50">
              <span className="text-4xl font-bold text-primary/60">
                {getInitials(persona?.name)}
              </span>
            </div>
          </div>
        )}
        
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Name and quote overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h3 className="text-xl font-bold drop-shadow-md leading-tight">
            {persona?.name || 'Nome da Persona'}
          </h3>
          {persona?.quote && (
            <p className="text-sm text-white/90 italic mt-1.5 line-clamp-2 drop-shadow-sm">
              &ldquo;{persona.quote}&rdquo;
            </p>
          )}
          {isGa4Based && (
            <Badge variant="secondary" className="mt-2 text-xs bg-white/20 text-white border-white/30 backdrop-blur-sm">
              <Sparkles className="h-3 w-3 mr-1" />
              Baseada em GA4
            </Badge>
          )}
        </div>
      </div>

      {/* Right column - Structured data */}
      <div className="flex-1 p-5 space-y-4 overflow-y-auto">
        {/* Demographics */}
        {hasDemographics && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Demografia</h4>
            <div className="flex flex-wrap gap-2">
              {persona?.age_range && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-secondary/50 rounded-md text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{persona.age_range}</span>
                </div>
              )}
              {persona?.gender && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-secondary/50 rounded-md text-sm">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{persona.gender}</span>
                </div>
              )}
              {persona?.location && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-secondary/50 rounded-md text-sm" title={persona.location}>
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate max-w-[120px]">{persona.location}</span>
                </div>
              )}
              {persona?.job_role && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-secondary/50 rounded-md text-sm" title={persona.job_role}>
                  <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate max-w-[120px]">{persona.job_role}</span>
                </div>
              )}
              {persona?.device_preference && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-secondary/50 rounded-md text-sm">
                  <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{persona.device_preference}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Goals */}
        {hasGoals && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" />
              Objetivos
            </h4>
            <ul className="text-sm space-y-1">
              {(expanded ? persona.goals : persona.goals.slice(0, 3)).map((goal, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary shrink-0 mt-0.5">•</span>
                  <span className="text-foreground/80 line-clamp-2">{goal}</span>
                </li>
              ))}
              {!expanded && persona.goals.length > 3 && (
                <li className="text-xs text-muted-foreground pl-4">
                  +{persona.goals.length - 3} mais...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Habits */}
        {hasHabits && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              Hábitos de uso
            </h4>
            <ul className="text-sm space-y-1">
              {(expanded ? persona.learning_habits : persona.learning_habits.slice(0, 3)).map((habit, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blue-500 shrink-0 mt-0.5">•</span>
                  <span className="text-foreground/80 line-clamp-2">{habit}</span>
                </li>
              ))}
              {!expanded && persona.learning_habits.length > 3 && (
                <li className="text-xs text-muted-foreground pl-4">
                  +{persona.learning_habits.length - 3} mais...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Frustrations */}
        {hasFrustrations && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Frown className="h-3.5 w-3.5" />
              Frustrações
            </h4>
            <ul className="text-sm space-y-1">
              {(expanded ? persona.frustrations : persona.frustrations.slice(0, 3)).map((frustration, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-500 shrink-0 mt-0.5">•</span>
                  <span className="text-foreground/80 line-clamp-2">{frustration}</span>
                </li>
              ))}
              {!expanded && persona.frustrations.length > 3 && (
                <li className="text-xs text-muted-foreground pl-4">
                  +{persona.frustrations.length - 3} mais...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Journey Key Pages */}
        {hasJourneyPages && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Route className="h-3.5 w-3.5" />
              Páginas-chave (TOP 3)
            </h4>
            <div className="flex flex-col gap-1.5">
              {(expanded ? persona.journey_key_pages : persona.journey_key_pages.slice(0, 3)).map((page, i) => (
                <span 
                  key={i} 
                  className="inline-block px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium truncate"
                  title={page}
                >
                  {page}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Journey Entry Context */}
        {hasJourneyEntry && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              Como chega
            </h4>
            <p className="text-sm text-foreground/80 line-clamp-2 bg-secondary/30 rounded-md p-2">
              {persona.journey_entry_context}
            </p>
          </div>
        )}

        {/* Journey Critical Point */}
        {hasJourneyCritical && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Ponto crítico
            </h4>
            <p className="text-sm text-amber-700 line-clamp-2 bg-amber-50 rounded-md p-2 border border-amber-200">
              {persona.journey_critical_point}
            </p>
          </div>
        )}

        {/* Empty state message */}
        {!hasDemographics && !hasGoals && !hasHabits && !hasFrustrations && !hasJourneyPages && !hasJourneyEntry && !hasJourneyCritical && (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <User className="h-12 w-12 mb-2 opacity-30" />
            <p className="text-sm">Preencha os dados da persona</p>
          </div>
        )}
      </div>
    </div>
  );

  // If clickable (listing page), wrap in a button-like div
  if (isClickable) {
    return (
      <div
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        role="button"
        tabIndex={0}
        className={`group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer border border-border/50 hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 overflow-hidden ${className}`}
        id={exportId}
      >
        {cardContent}
      </div>
    );
  }

  // Non-clickable version (edit page preview)
  return (
    <div id={exportId} className={`rounded-xl border border-border/50 overflow-hidden bg-white ${className}`}>
      {cardContent}
    </div>
  );
};

export default PersonaPreviewCard;
